import apiClient from './axios';
import { ApiResponse, PaginatedResponse } from '../types';

/** Which app somebody joined the marketing site's waitlist for. */
export type WaitlistSegment = 'personal' | 'business';

export interface WaitlistEntry {
  id: string;
  email: string;
  fullName: string | null;
  phone: string | null;
  segment: WaitlistSegment;
  /** Only ever set on a business sign-up. */
  businessName: string | null;
  /** Which CTA on the site: `hero`, `footer`, `pricing`… */
  source: string | null;
  referrer: string | null;
  createdAt: string;
}

export interface WaitlistStats {
  total: number;
  bySegment: Record<WaitlistSegment, number>;
  last7Days: number;
  last7DaysBySegment: Record<WaitlistSegment, number>;
}

export interface GetWaitlistParams {
  page?: number;
  limit?: number;
  segment?: WaitlistSegment;
  search?: string;
}

/** Drops empty values — the backend 400s on params it does not know, and
 *  an empty `segment` is not one of the values it does. */
function compact<T extends object>(params: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(params).filter(
      ([, value]) => value !== undefined && value !== null && value !== '',
    ),
  ) as Partial<T>;
}

export const waitlistApi = {
  list: async (params: GetWaitlistParams = {}) => {
    const { data } = await apiClient.get<
      ApiResponse<PaginatedResponse<WaitlistEntry>>
    >('/admin/waitlist', { params: compact(params) });
    return data.data;
  },

  stats: async () => {
    const { data } = await apiClient.get<ApiResponse<WaitlistStats>>(
      '/admin/waitlist/stats',
    );
    return data.data;
  },

  /** The filtered list as CSV, newest first. */
  exportCsv: async (
    params: Pick<GetWaitlistParams, 'segment' | 'search'> = {},
  ) => {
    const { data } = await apiClient.get<Blob>('/admin/waitlist/export', {
      params: compact(params),
      responseType: 'blob',
    });
    return data;
  },
};
